import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, FormSection, type SortState } from '../patterns';
import { getTenantInfo } from '../../services/tenantService';
import { formatDateTime, formatTime } from '../../dateFormat';

/**
 * Read-only Weather History dashboard for Manager / Farm Admin roles.
 *
 * Renders the last 48 hours of WeatherHistory rows for the authenticated
 * tenant in two complementary views: a sortable hourly log table and an
 * inline SVG trend chart showing temperature, wind, and precipitation as
 * three normalised line series. Backed by the same
 * GET /api/v1/tenants/me/weather/recent endpoint that powers the PWA's
 * 3-hour cache (Phase 4 / Task 3); we just request hours=48 here.
 *
 * Track: automatic_weather_tracking_20260428 / Phase 4 / Task 5.
 * Spec: Section 5 (UI & UX) + RBAC `viewWeatherHistory`.
 *
 * Phase C2 / Task 13: page now wraps its content in App Shell chrome —
 * <PageHeader> with title + Reload action, a 3-segment breadcrumb pushed
 * via useBreadcrumb, and the chart/table moved into pat-* primitives
 * (FormSection + DataTable). The SVG trend chart itself is preserved
 * verbatim — there's no pat-* equivalent for a custom domain chart.
 */

interface WeatherHistoryEntry {
  timestamp: string;
  temp?: number;
  windSpeed?: number;
  precipitation?: number;
  condition?: string;
  humidity?: number;
  pressure?: number;
}

export const HOURS = 48;
export const CHART_WIDTH = 720;
export const CHART_HEIGHT = 250;
export const CHART_PADDING = 28;
export const PLOT_BOTTOM = CHART_HEIGHT - 58; // 192 — preserves the original plot area
export const EDGE_LABEL_DROP = 24;             // labels within this many px of plot left/right edge are dropped

/**
 * Map a millisecond timestamp to its SVG X coordinate inside the chart's plot area.
 * `earliestMs` pins to the left plot edge; `latestMs` pins to the right plot edge.
 * Falls back to a span of 1 when earliest === latest to avoid division by zero.
 */
export function xForTimestamp(
  tsMs: number,
  earliestMs: number,
  latestMs: number
): number {
  const span = latestMs - earliestMs || 1;
  return CHART_PADDING + ((tsMs - earliestMs) / span) * (CHART_WIDTH - CHART_PADDING * 2);
}

type SortDir = 'desc' | 'asc';

const formatNumber = (n?: number): string =>
  typeof n === 'number' ? `${n}` : '—';

interface SeriesSpec {
  key: 'temp' | 'windSpeed' | 'precipitation';
  colour: string;
  labelKey: string;
}

const SERIES: SeriesSpec[] = [
  { key: 'temp', colour: 'var(--bs-danger)', labelKey: 'spray.weatherHistory.legendTemp' },
  { key: 'windSpeed', colour: 'var(--bs-primary)', labelKey: 'spray.weatherHistory.legendWind' },
  { key: 'precipitation', colour: 'var(--bs-teal)', labelKey: 'spray.weatherHistory.legendPrecip' },
];

/**
 * Per-series stats used by both the chart geometry and the legend's
 * "range" annotation. Values are normalised onto the same chart axis
 * (3 different units share one space), so the legend is what tells the
 * user what each line's min/max actually means.
 */
interface SeriesStats {
  min: number;
  max: number;
}

function computeStats(
  entries: WeatherHistoryEntry[],
  key: SeriesSpec['key']
): SeriesStats | null {
  const values = entries
    .map(e => e[key])
    .filter((v): v is number => typeof v === 'number');
  if (values.length === 0) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * Build a smoothed SVG path for a single metric using the Fritsch–Carlson
 * monotone cubic interpolation method. Unlike Catmull-Rom (which can swing
 * past local extrema and produce "tooth" overshoots on sharp peaks like
 * precipitation 0 → 1.5 → 0), monotone cubic guarantees the curve stays
 * within the bounds of each segment's endpoints — no overshoot, no
 * undershoot, and any flat segment renders truly flat.
 */
function buildSmoothPath(
  entries: WeatherHistoryEntry[],
  key: SeriesSpec['key'],
  stats: SeriesStats | null,
  earliestMs: number,
  latestMs: number
): string {
  if (!stats) return '';
  const range = stats.max - stats.min || 1;

  const usableH = PLOT_BOTTOM - CHART_PADDING;

  const sorted = [...entries].sort(
    (a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp)
  );

  const points: Array<[number, number]> = [];
  sorted.forEach((e) => {
    const v = e[key];
    if (typeof v !== 'number') return;
    const x = xForTimestamp(Date.parse(e.timestamp), earliestMs, latestMs);
    const y = CHART_PADDING + (1 - (v - stats.min) / range) * usableH;
    points.push([x, y]);
  });

  const n = points.length;
  if (n === 0) return '';
  if (n === 1) {
    const [x, y] = points[0];
    return `M${x.toFixed(1)},${y.toFixed(1)}`;
  }

  // Fritsch–Carlson monotone cubic tangents.
  // Step 1: per-segment slopes Δy/Δx.
  const slopes: number[] = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    const dx = points[i + 1][0] - points[i][0] || 1;
    slopes[i] = (points[i + 1][1] - points[i][1]) / dx;
  }
  // Step 2: initial tangents — average of adjacent slopes, endpoints copy.
  const tangents: number[] = new Array(n);
  tangents[0] = slopes[0];
  tangents[n - 1] = slopes[n - 2];
  for (let i = 1; i < n - 1; i++) {
    tangents[i] = (slopes[i - 1] + slopes[i]) / 2;
  }
  // Step 3: monotonicity correction — kill tangents at extrema and rescale
  // when (a², b²) leaves the unit-circle-of-radius-3 region.
  for (let i = 0; i < n - 1; i++) {
    if (slopes[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
      continue;
    }
    const a = tangents[i] / slopes[i];
    const b = tangents[i + 1] / slopes[i];
    const h = a * a + b * b;
    if (h > 9) {
      const tau = 3 / Math.sqrt(h);
      tangents[i] = tau * a * slopes[i];
      tangents[i + 1] = tau * b * slopes[i];
    }
  }

  // Step 4: emit cubic Bezier segments. For each segment, the two control
  // points sit at 1/3 of dx along the tangent line — this is the standard
  // Hermite → Bezier conversion for monotone cubic.
  const segments = [`M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`];
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    const dx = (x1 - x0) / 3;
    const cp1x = x0 + dx;
    const cp1y = y0 + dx * tangents[i];
    const cp2x = x1 - dx;
    const cp2y = y1 - dx * tangents[i + 1];
    segments.push(
      `C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${x1.toFixed(1)},${y1.toFixed(1)}`
    );
  }
  return segments.join(' ');
}

const formatRange = (stats: SeriesStats | null): string =>
  stats ? `${stats.min}–${stats.max}` : '—';

interface XTick {
  x: number;
  primary: string;
  secondary?: string;
  isDayBoundary: boolean;
  tsMs: number;
}

/**
 * Format a millisecond timestamp as the axis's day-boundary secondary label:
 * short month + numeric day, deliberately WITHOUT a weekday. This is a thin
 * local formatter rather than the shared `formatDateShort` — that helper's
 * SHORT_OPTS includes `weekday: 'short'`, which is a heavier label than this
 * axis needs (and would break the tight tick layout).
 *
 * Locale is pinned to 'en' (rather than following the machine/date-format
 * preset locale) so the "Mon D" ordering is stable — some locales (e.g.
 * en-GB/en-ZA) render day-before-month for this option combination, which
 * would make the compact axis label read oddly next to the HH:MM primary
 * label above it.
 */
function formatAxisDayLabel(ms: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en', {
    timeZone,
    month: 'short',
    day: 'numeric',
  }).format(new Date(ms));
}

/**
 * Build evenly-spaced ticks across [earliestMs, latestMs] in the given timezone.
 * Tick wall-clock hours are aligned to `intervalHours` boundaries (e.g. 6h → 00/06/12/18).
 * The first tick is the next aligned hour STRICTLY AFTER earliestMs; the last tick is
 * the latest aligned hour ≤ latestMs.
 *
 * Timezone math is vanilla JS via Intl.DateTimeFormat.formatToParts. The wall-clock-to-ms
 * conversion uses a one-shot offset lookup: exact for any moment outside a DST transition
 * window. Within a DST transition window in a DST-observing zone, one tick may be off by
 * one hour — documented edge case (Africa/Johannesburg does not observe DST).
 */
function buildXTicks(
  earliestMs: number,
  latestMs: number,
  timeZone: string | undefined,
  intervalHours: number = 6
): XTick[] {
  if (!(latestMs > earliestMs)) return [];

  const partsFmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const readParts = (ms: number) => {
    const p = partsFmt.formatToParts(new Date(ms));
    const get = (t: string) => parseInt(p.find((x) => x.type === t)!.value, 10);
    return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour') };
  };

  // Convert wall-clock (Y, M, D, H, 0, 0) in `timeZone` back to real UTC ms.
  const wallClockToMs = (y: number, mo: number, d: number, h: number): number => {
    const naive = Date.UTC(y, mo - 1, d, h, 0, 0);
    const np = readParts(naive);
    const naiveWallclock = Date.UTC(np.y, np.mo - 1, np.d, np.h, 0, 0);
    const offset = naiveWallclock - naive;
    return naive - offset;
  };

  // Advance the calendar day in-place via ms arithmetic to avoid manual month-length math.
  const advanceDay = (state: { y: number; mo: number; d: number }) => {
    const dayStart = wallClockToMs(state.y, state.mo, state.d, 0);
    const next = readParts(dayStart + 24 * 3600_000 + 3600_000); // +25h to skip DST forward gaps
    state.y = next.y;
    state.mo = next.mo;
    state.d = next.d;
  };

  // Start candidate at first interval-aligned hour STRICTLY after earliestMs.
  const p0 = readParts(earliestMs);
  let h = Math.floor(p0.h / intervalHours) * intervalHours + intervalHours;
  const day = { y: p0.y, mo: p0.mo, d: p0.d };
  while (h >= 24) {
    h -= 24;
    advanceDay(day);
  }

  const ticks: XTick[] = [];
  for (let safety = 0; safety < 64; safety++) {
    const ms = wallClockToMs(day.y, day.mo, day.d, h);
    if (ms > latestMs) break;
    if (ms > earliestMs) {
      ticks.push({
        x: xForTimestamp(ms, earliestMs, latestMs),
        primary: formatTime(new Date(ms), { timeZone }),
        secondary: h === 0 ? formatAxisDayLabel(ms, timeZone) : undefined,
        isDayBoundary: h === 0,
        tsMs: ms,
      });
    }
    h += intervalHours;
    while (h >= 24) {
      h -= 24;
      advanceDay(day);
    }
  }
  return ticks;
}

const WeatherHistoryDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<WeatherHistoryEntry[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [reloadCounter, setReloadCounter] = useState(0);
  const [farmTimezone, setFarmTimezone] = useState<string | undefined>(undefined);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.tabs.weatherHistory') },
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');

    const weatherP = axios.get<{ entries: WeatherHistoryEntry[]; hours: number }>(
      '/api/v1/tenants/me/weather/recent',
      { params: { hours: HOURS }, withCredentials: true }
    );
    // Soft-fallback: a failing tenant fetch must not break the chart.
    const tenantP = getTenantInfo().catch(() => undefined);

    Promise.all([weatherP, tenantP])
      .then(([weatherRes, tenant]) => {
        if (cancelled) return;
        setEntries(weatherRes.data?.entries || []);
        setFarmTimezone(
          tenant && typeof tenant.farmTimezone === 'string' && tenant.farmTimezone
            ? tenant.farmTimezone
            : undefined
        );
      })
      .catch(() => {
        // Weather fetch failed (tenant failure already swallowed above).
        if (cancelled) return;
        setLoadError(t('spray.weatherHistory.loadError'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // t is intentionally omitted — it changes reference on every render and
    // would re-fire this fetch loop. The error message is only read inside
    // the catch, which the closure captures for the lifetime of one fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadCounter]);

  const sortedEntries = useMemo(() => {
    if (!entries) return [];
    const copy = [...entries];
    copy.sort((a, b) => {
      const da = Date.parse(a.timestamp);
      const db = Date.parse(b.timestamp);
      return sortDir === 'desc' ? db - da : da - db;
    });
    return copy;
  }, [entries, sortDir]);

  const seriesStats = useMemo<Record<SeriesSpec['key'], SeriesStats | null>>(() => ({
    temp: computeStats(sortedEntries, 'temp'),
    windSpeed: computeStats(sortedEntries, 'windSpeed'),
    precipitation: computeStats(sortedEntries, 'precipitation'),
  }), [sortedEntries]);

  const { earliestMs, latestMs } = useMemo(() => {
    if (!sortedEntries.length) return { earliestMs: 0, latestMs: 0 };
    const times = sortedEntries.map((e) => Date.parse(e.timestamp));
    return { earliestMs: Math.min(...times), latestMs: Math.max(...times) };
  }, [sortedEntries]);

  const ticks = useMemo(
    () => buildXTicks(earliestMs, latestMs, farmTimezone),
    [earliestMs, latestMs, farmTimezone]
  );

  const handleReload = () => setReloadCounter(n => n + 1);
  const sortState: SortState = { key: 'timestamp', direction: sortDir === 'desc' ? 'desc' : 'asc' };
  const handleSortChange = (next: SortState) => {
    if (next.key !== 'timestamp') return;
    setSortDir(next.direction === 'asc' ? 'asc' : 'desc');
  };

  if (loading) {
    return (
      <div className="text-center p-4" data-testid="weather-history-dashboard">
        <Spinner animation="border" role="status" />
        <div className="mt-2">{t('common.loading')}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div data-testid="weather-history-dashboard">
        <PageHeader
          title={t('spray.weatherHistory.title')}
          actions={
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handleReload}
            >
              {t('spray.weatherHistory.reload')}
            </button>
          }
        />
        <Alert variant="danger" role="alert">{loadError}</Alert>
      </div>
    );
  }

  const empty = !entries || entries.length === 0;
  const readingsCount = entries?.length ?? 0;

  return (
    <div data-testid="weather-history-dashboard">
      <PageHeader
        title={t('spray.weatherHistory.title')}
        meta={t('spray.weatherHistory.meta', { count: readingsCount })}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={handleReload}
          >
            {t('spray.weatherHistory.reload')}
          </button>
        }
      />

      {empty ? (
        <Alert variant="info">{t('spray.weatherHistory.empty')}</Alert>
      ) : (
        <>
          <FormSection id="weather-trend" title={t('spray.weatherHistory.chartTitle')}>
            <svg
              data-testid="weather-trend-chart"
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              width="100%"
              height={CHART_HEIGHT}
              role="img"
              aria-label={t('spray.weatherHistory.chartTitle')}
            >
              <rect
                x={CHART_PADDING}
                y={CHART_PADDING}
                width={CHART_WIDTH - CHART_PADDING * 2}
                height={PLOT_BOTTOM - CHART_PADDING}
                fill="var(--bs-light)"
                stroke="var(--bs-border-color)"
              />
              {ticks.filter((tk) => tk.isDayBoundary).map((tk) => (
                <line
                  key={`gridline-${tk.tsMs}`}
                  data-role="midnight-gridline"
                  x1={tk.x}
                  y1={CHART_PADDING}
                  x2={tk.x}
                  y2={PLOT_BOTTOM}
                  stroke="var(--neutral-400)"
                  strokeWidth={1}
                  strokeDasharray="2 4"
                />
              ))}
              {SERIES.map(spec => (
                <path
                  key={spec.key}
                  data-series={spec.key}
                  d={buildSmoothPath(sortedEntries, spec.key, seriesStats[spec.key], earliestMs, latestMs)}
                  fill="none"
                  stroke={spec.colour}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              <g data-testid="weather-trend-x-axis">
                <line
                  data-role="axis-baseline"
                  x1={CHART_PADDING}
                  y1={PLOT_BOTTOM}
                  x2={CHART_WIDTH - CHART_PADDING}
                  y2={PLOT_BOTTOM}
                  stroke="var(--bs-border-color)"
                  strokeWidth={1}
                />
                {ticks.map((tk) => {
                  const isNearEdge =
                    tk.x - CHART_PADDING < EDGE_LABEL_DROP ||
                    (CHART_WIDTH - CHART_PADDING) - tk.x < EDGE_LABEL_DROP;
                  return (
                    <g
                      key={tk.tsMs}
                      data-tick-x={tk.x.toFixed(1)}
                      data-day-boundary={tk.isDayBoundary ? 'true' : undefined}
                    >
                      <line
                        data-role="tick-mark"
                        x1={tk.x}
                        y1={PLOT_BOTTOM}
                        x2={tk.x}
                        y2={PLOT_BOTTOM + 6}
                        stroke="var(--neutral-400)"
                        strokeWidth={1}
                      />
                      {!isNearEdge && (
                        <text
                          data-role="primary-label"
                          x={tk.x}
                          y={PLOT_BOTTOM + 18}
                          textAnchor="middle"
                          fontSize={11}
                          fill="var(--neutral-700)"
                        >
                          {tk.primary}
                        </text>
                      )}
                      {!isNearEdge && tk.secondary && (
                        <text
                          data-role="secondary-label"
                          x={tk.x}
                          y={PLOT_BOTTOM + 32}
                          textAnchor="middle"
                          fontSize={10}
                          fill="var(--bs-muted)"
                        >
                          {tk.secondary}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            </svg>
            <div className="d-flex flex-wrap gap-3 mt-2 small">
              {SERIES.map(spec => (
                <span
                  key={spec.key}
                  className="d-flex align-items-center gap-1"
                  data-testid={`legend-${spec.key}`}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      background: spec.colour,
                    }}
                    aria-hidden="true"
                  />
                  <span>{t(spec.labelKey)}</span>
                  <span className="text-muted">· {formatRange(seriesStats[spec.key])}</span>
                </span>
              ))}
            </div>
          </FormSection>

          <FormSection id="weather-readings" title={t('spray.weatherHistory.readings')}>
            <DataTable<WeatherHistoryEntry>
              rows={sortedEntries}
              rowKey={(e) => e.timestamp}
              sort={sortState}
              onSortChange={handleSortChange}
              columns={[
                {
                  key: 'timestamp',
                  label: t('spray.weatherHistory.colTimestamp'),
                  sortable: true,
                  render: (e) => formatDateTime(e.timestamp),
                },
                {
                  key: 'temp',
                  label: t('spray.weatherHistory.colTemp'),
                  align: 'right',
                  render: (e) => formatNumber(e.temp),
                },
                {
                  key: 'windSpeed',
                  label: t('spray.weatherHistory.colWind'),
                  align: 'right',
                  render: (e) => formatNumber(e.windSpeed),
                },
                {
                  key: 'precipitation',
                  label: t('spray.weatherHistory.colPrecip'),
                  align: 'right',
                  render: (e) => formatNumber(e.precipitation),
                },
                {
                  key: 'condition',
                  label: t('spray.weatherHistory.colCondition'),
                  render: (e) => e.condition ?? '—',
                },
                {
                  key: 'humidity',
                  label: t('spray.weatherHistory.colHumidity'),
                  align: 'right',
                  render: (e) => formatNumber(e.humidity),
                },
                {
                  key: 'pressure',
                  label: t('spray.weatherHistory.colPressure'),
                  align: 'right',
                  render: (e) => formatNumber(e.pressure),
                },
              ]}
            />
          </FormSection>
        </>
      )}
    </div>
  );
};

export default WeatherHistoryDashboard;
